import styled, { keyframes } from "styled-components";

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 460px;
  max-height: 90dvh;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  isolation: isolate;
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ModalContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

export const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;

  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const ModalSubtitle = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.005em;
  line-height: normal;
`;

export const CloseButton = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  svg {
    width: 14px;
    height: 14px;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const AvailableCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
`;

export const AvailableLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const AvailableValue = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.015em;
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

export const Label = styled.p`
  margin: 0;
  text-align: left;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const Input = styled.input`
  display: flex;
  width: 100%;
  height: 48px;
  padding: 0 18px;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-input);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.005em;
  outline: none;
  transition:
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &::placeholder {
    color: var(--color-text-dim);
    font-weight: 600;
  }

  &:focus {
    border-color: var(--color-line-strong);
    box-shadow: var(--shadow-micro), var(--shadow-glow);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Hide number spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

export const List = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ListItem = styled.li`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12.5px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  width: 100%;
  margin-top: 4px;
`;

export const CancelButton = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding: 0 20px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
    color: var(--color-brand-700);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ConfirmButton = styled.button`
  flex: 1.4;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow: var(--shadow-cta);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;
